import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ConnectorJobStatus } from '@/models/connectors';
import { useGetConnectorFlowInstanceJobsQuery } from '@/services/endpoints/workspace-connectors';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeAgo } from '@/components/time-ago';

type SourceConnectorJobHistoryTableProps = {
  connectorFlowInstanceId?: string;
  pollInterval?: number;
};

const STATUS_COLORS: Record<ConnectorJobStatus, string> = {
  SUCCESS: 'text-green-700',
  PARTIAL_SUCCESS: 'text-amber-700',
  FAILED: 'text-red-600',
  STARTED: 'text-blue-700',
  IN_PROGRESS: 'text-blue-700',
  CANCELLED: 'text-gray-600',
  TIMEOUT: 'text-orange-700',
};

export const SourceConnectorJobHistoryTable: FC<SourceConnectorJobHistoryTableProps> = ({
  connectorFlowInstanceId,
  pollInterval = 0,
}) => {
  const { t } = useTranslation('data_sources');
  const { id: workspaceId } = useWorkspace();

  const { data: jobs = [], isLoading, isFetching } = useGetConnectorFlowInstanceJobsQuery(
    { workspaceId, connectorFlowInstanceId: connectorFlowInstanceId ?? '', limit: 20 },
    {
      skip: !connectorFlowInstanceId,
      pollingInterval: pollInterval,
    }
  );

  if (!connectorFlowInstanceId) {
    return <p className='text-sm text-secondary-600'>{t('detail.source_connector.no_flow_instance')}</p>;
  }

  return (
    <Loadable isLoading={isLoading}>
      <div className='flex flex-col gap-3'>
        <h4 className='text-sm font-semibold text-black-100'>{t('detail.source_connector.job_history')}</h4>

        {jobs.length === 0 ? (
          <p className='text-sm text-secondary-600'>{t('detail.source_connector.no_jobs')}</p>
        ) : (
          <div className='overflow-x-auto rounded-xl border'>
            <table className='min-w-full text-left text-sm'>
              <thead className='bg-secondary-background'>
                <tr>
                  <th className='px-4 py-2 font-semibold'>{t('detail.source_connector.job_status')}</th>
                  <th className='px-4 py-2 font-semibold'>{t('detail.source_connector.job_operation')}</th>
                  <th className='px-4 py-2 font-semibold'>{t('detail.source_connector.job_records')}</th>
                  <th className='px-4 py-2 font-semibold'>{t('detail.source_connector.job_started')}</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} className='border-t'>
                    <td className={`px-4 py-2 font-medium ${STATUS_COLORS[job.status]}`}>{job.status}</td>
                    <td className='px-4 py-2'>{job.operation ?? '—'}</td>
                    <td className='px-4 py-2'>
                      {job.successfulRecords}/{job.totalRecords}
                      {job.failedRecords > 0 && ` (${job.failedRecords} failed)`}
                    </td>
                    <td className='px-4 py-2'>
                      <TimeAgo time={job.completedAt || job.createdAt} isShowTimeElapses />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isFetching && pollInterval > 0 && (
          <p className='text-xs text-secondary-500'>{t('detail.source_connector.polling_jobs')}</p>
        )}
      </div>
    </Loadable>
  );
};
